import { activeSupportersQuery, membersServedQuery, mrrQuery } from "@/api/reporting";
import { api } from "@/api/utils";
import { ChartBox, formatAxisNumber, useContainerSize } from "@/components/charts/ChartBox";
import { centsToDollars } from "@common/utils/currency";
import { formatWholeNumber } from "@common/utils/i18n";
import { Box, BoxProps, Flex, Grid, Text } from "@givenwell/components";
import { RevenueStreamRow } from "@givenwell/management-api";
import { styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { areaY, defineChart, dot, lineY, rect, ruleX, ruleY, stack, text } from "@tanstack/charts";
import { crosshair } from "@tanstack/charts/crosshair";
import { decorative } from "@tanstack/charts/mark/decorative";
import { Chart } from "@tanstack/charts/react";
import { Chart as TooltipChart } from "@tanstack/charts/react/tooltip";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip } from "@tanstack/charts/tooltip";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { scaleUtc } from "d3-scale";
import { Fragment, ReactNode, Suspense, useMemo } from "react";
import { REVENUE_STREAM_COLOR, REVENUE_STREAM_ORDER, REVENUE_STREAMS, toRevenueGroup } from "./revenueStreams";
import { ChartDataPoint } from "./utils";

export const Route = createFileRoute("/dashboard/")({
  component: Page,
});

function Page() {
  return (
    <Grid
      css={{
        width: "100%",
        height: "100%",
        gap: 12,
        gridCols: 2,
        gridTemplateRows: "repeat(2, 1fr)",
      }}
    >
      <Suspense fallback={<Card css={{ flex: "1 0 0px" }} />}>
        <MRRCard />
      </Suspense>
      <Flex css={{ flexDirection: "column", gap: 12 }}>
        <Suspense fallback={<Card css={{ flex: "1 0 0px" }} />}>
          <MembersServed />
        </Suspense>
      </Flex>
      <Suspense fallback={<Card />}>
        <ActiveSupporters />
      </Suspense>
      <Suspense fallback={<Card />}>
        <CurrencyRatioChart />
      </Suspense>
    </Grid>
  );
}

// Shared chart helpers

// Month boundaries within [start, end], in UTC - grid lines and tick labels land on these, matching
// the old Plot `ticks: "month"` / `gridX({interval: "month"})` behaviour.
function monthStartsBetween(start: Date, end: Date): Date[] {
  const months: Date[] = [];
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  while (cursor.getTime() < start.getTime()) {
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  while (cursor.getTime() <= end.getTime()) {
    months.push(new Date(cursor));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return months;
}

const monthTickFormatter = new Intl.DateTimeFormat("en-GB", { month: "short", timeZone: "UTC" });
// Short month names, with the year at January boundaries (mirroring d3/Plot's multi-scale format).
const formatMonthTick = (date: Date) =>
  date.getUTCMonth() === 0 ? String(date.getUTCFullYear()) : monthTickFormatter.format(date);

// The semi-transparent white rect painted over the "future"/projected region of a chart.
type VeilRegion = { start: Date; end: Date };

// MRR

const endDate = new Date();
endDate.setMonth(endDate.getMonth() + 2);
endDate.setDate(0);
endDate.setHours(23, 59, 59, 999);

function localToUTC(date: Date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
}

const MRR_TARGET_TICKS = 10;

// An auto-scaled domain topping out mid-step (e.g. 157k with a 20k step) leaves the tallest bars
// above the last gridline and looking clipped. Round the domain up to the next step so a labelled
// line always sits at or above the tallest stack.
function niceStackedMax(rows: RevenueStreamRow[] | undefined, currency: string) {
  if (!rows?.length) return undefined;

  const totalByMonth = new Map<string, number>();
  for (const row of rows) {
    const dollars = centsToDollars(row.total || 0, currency);
    totalByMonth.set(row.month, (totalByMonth.get(row.month) ?? 0) + dollars);
  }

  const max = Math.max(...totalByMonth.values());
  if (!(max > 0)) return undefined;

  // Derive the step from the tick count we want, snapped to a 1/2/5 multiple. Deriving it from the
  // magnitude alone would jump from a 5k to a 50k step the moment the total crossed 100k.
  const rawStep = max / MRR_TARGET_TICKS;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  const step = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;

  const rounded = Math.ceil(max / step) * step;
  // A max landing exactly on the top gridline reads as clipped, so give it one more step.
  return rounded === max ? rounded + step : rounded;
}

// One row per month carrying every stream group plus the stacked total, for a single unified tooltip
// on hover (same shape as the Members Served card's `dailyTotals`).
type MRRMonthTotals = {
  month: Date;
  // Anchor for the focus marks: the 15th, so the crosshair rule lands inside the bar rather than on
  // the boundary between two of them.
  midMonth: Date;
  total: number;
  byStream: Record<string, number>;
};

// Derive both dates from the leading YYYY-MM of the API's month string rather than from a parsed Date.
// `new Date("2026-02-01T00:00:00")` (no Z) is parsed as *local* time, which in NZ lands on 31 Jan UTC -
// reading UTC month parts off that would anchor the tooltip to the wrong bar.
function parseMonth(month: string) {
  const [year, monthNumber] = month.split("-").map(Number);
  if (!year || !monthNumber) {
    // Unexpected format; fall back to native parsing so we degrade rather than crash.
    const parsed = new Date(month);
    return { start: parsed, mid: parsed };
  }
  return {
    start: new Date(Date.UTC(year, monthNumber - 1, 1)),
    mid: new Date(Date.UTC(year, monthNumber - 1, 15)),
  };
}

function getMonthlyTotals(rows: RevenueStreamRow[] | undefined, currency: string): MRRMonthTotals[] {
  const byMonth = new Map<string, MRRMonthTotals>();

  for (const row of rows ?? []) {
    let entry = byMonth.get(row.month);
    if (!entry) {
      const { start, mid } = parseMonth(row.month);
      entry = { month: start, midMonth: mid, total: 0, byStream: {} };
      byMonth.set(row.month, entry);
    }
    const group = toRevenueGroup(row.stream);
    const dollars = centsToDollars(row.total || 0, currency);
    entry.byStream[group] = (entry.byStream[group] ?? 0) + dollars;
    entry.total += dollars;
  }

  return [...byMonth.values()].sort((a, b) => a.month.getTime() - b.month.getTime());
}

// One stacked bar segment: a month-wide rect from y1 to y2 for one revenue group, carrying the whole
// month's totals so the tooltip can render every stream from any focused segment.
type MRRSegment = {
  month: Date;
  midMonth: Date;
  monthEnd: Date;
  group: string;
  y1: number;
  y2: number;
  totals: MRRMonthTotals;
};

// Stack bottom -> top in REVENUE_STREAM_ORDER (any unexpected group lands on top), mirroring the old
// Plot rectY `order` option.
function getMRRSegments(monthlyTotals: MRRMonthTotals[]): MRRSegment[] {
  const segments: MRRSegment[] = [];
  for (const totals of monthlyTotals) {
    const monthEnd = new Date(Date.UTC(totals.month.getUTCFullYear(), totals.month.getUTCMonth() + 1, 1));
    const groups = [
      ...REVENUE_STREAM_ORDER.filter(group => group in totals.byStream),
      ...Object.keys(totals.byStream).filter(group => !REVENUE_STREAM_ORDER.includes(group)),
    ];
    let cumulative = 0;
    for (const group of groups) {
      const amount = totals.byStream[group] ?? 0;
      segments.push({
        month: totals.month,
        midMonth: totals.midMonth,
        monthEnd,
        group,
        y1: cumulative,
        y2: cumulative + amount,
        totals,
      });
      cumulative += amount;
    }
  }
  return segments;
}

function MRRCard() {
  const { data } = useQuery(
    mrrQuery({
      amortizeOneOffs: true,
      amortizeRecurring: true,
      consolidated: true,
      includeGivenwell: false,
      endDate: endDate,
    }),
  );
  // Consolidated rolls everything into NZD; per-currency would use the row currency.
  const displayCurrency = "NZD";

  const { yMax, segments, colorDomain, colorRange } = useMemo(() => {
    const yMax = niceStackedMax(data, displayCurrency);
    const segments = getMRRSegments(getMonthlyTotals(data, displayCurrency));
    const observed = new Set(segments.map(s => s.group));
    const colorDomain = [
      ...REVENUE_STREAM_ORDER.filter(group => observed.has(group)),
      ...[...observed].filter(group => !REVENUE_STREAM_ORDER.includes(group)),
    ];
    const colorRange = colorDomain.map(group => REVENUE_STREAM_COLOR[group] ?? colors.text3);
    return { yMax, segments, colorDomain, colorRange };
  }, [data, displayCurrency]);

  const definition = useMemo(() => {
    const veil: VeilRegion = { start: new Date(), end: localToUTC(endDate) };
    return defineChart({
      marks: [
        rect(segments, {
          id: "revenue",
          x: "midMonth",
          x1: "month",
          x2: "monthEnd",
          y1: "y1",
          y2: "y2",
          color: "group",
          key: s => `${s.month.toISOString()}-${s.group}`,
          inset: 0,
        }),
        // Veil the future months so they read as projection. Decorative: painted, but it emits no
        // interaction points, so the tooltip and focus only attach to the revenue segments.
        ...(yMax === undefined
          ? []
          : [
              decorative(
                rect([veil], {
                  x1: "start",
                  x2: "end",
                  y1: () => 0,
                  y2: () => yMax,
                  fill: "#ffffff66",
                  inset: 0,
                }),
              ),
            ]),
        // The vertical rule that follows the hovered month (the old Plot.pointerX ruleX).
        crosshair({ x: { stroke: colors.text3, strokeWidth: 1, strokeOpacity: 1 }, y: false }),
      ],
      scales: {
        x: {
          scale: scaleUtc,
          axis: { line: false, ticks: { size: 0 }, tickLabels: { fontSize: 12 } },
        },
        y: {
          // undefined while data is empty, letting the scale fall back to auto-scaling.
          scale: yMax === undefined ? scaleLinear : scaleLinear().domain([0, yMax]),
          grid: true,
          axis: {
            line: false,
            ticks: { count: MRR_TARGET_TICKS, size: 0, format: formatAxisNumber },
            tickLabels: { fontSize: 12 },
          },
        },
      },
      margin: { bottom: 40, left: 60 },
      color: { domain: colorDomain, range: colorRange },
      // Hovering snaps to the nearest month and focuses every segment in its stack.
      focus: "group-x",
      focusRing: false,
      maxFocusDistance: Number.POSITIVE_INFINITY,
      tooltip: {
        use: tooltip,
        // Columns run the full height of the card, so anchoring above the top of the stack would push
        // the tooltip out of view on the tallest months. Pin it to the chart's vertical centre and let
        // it follow the hovered column horizontally, flipping sides near the edges.
        anchor: { x: "value", y: "plot-center" },
        placement: ["right", "left"],
        offset: 12,
      },
    });
  }, [segments, yMax, colorDomain, colorRange]);

  const { ref, height } = useContainerSize();

  return (
    <Card title="MRR">
      <ValueDescription css={{ pb: 8 }}>All currencies (NZD), amortised</ValueDescription>
      <MRRChartLegend />
      <Box css={{ flex: "1 0 0px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <ChartBox
          ref={ref}
          css={{
            height: "100%",
            flex: "1 0 0px",
            overflow: "hidden",
            color: colors.text,
          }}
        >
          {height !== undefined && (
            <TooltipChart
              definition={definition}
              height={height}
              ariaLabel="MRR"
              renderTooltipBody={({ points, defaultBody }) => {
                const datum = points[0]?.datum;
                if (!datum || !("totals" in datum)) return defaultBody;
                return <MRRTooltip d={datum.totals} currency={displayCurrency} />;
              }}
            />
          )}
        </ChartBox>
      </Box>
    </Card>
  );
}

// "Jun 2026" - the bars are monthly, so no day component. Formatted in UTC to match the x scale.
const mrrTooltipMonthFormatter = new Intl.DateTimeFormat("en-GB", {
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

// Share of the month's stacked total. A stream that contributes something shouldn't round away to
// "0%", so anything below half a percent shows as "<1%".
function formatShare(amount: number, total: number) {
  if (!(total > 0)) return "0%";
  const share = (amount / total) * 100;
  if (share > 0 && share < 0.5) return "<1%";
  return `${Math.round(share)}%`;
}

function MRRTooltip({ d, currency }: { d: MRRMonthTotals; currency: string }) {
  // Whole dollars: these run to six figures, and cents would only add noise.
  const formatAmount = (dollars: number) =>
    new Intl.NumberFormat("en-NZ", {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
      maximumFractionDigits: 0,
    }).format(dollars);

  // Top-to-bottom in visual stack order (the stack builds bottom-up). Every stream is always listed,
  // zeroes included, so the tooltip keeps a fixed size and its rows don't shift between months.
  const rows = [...REVENUE_STREAMS].reverse();

  return (
    // A fixed three-column grid (swatch+label / amount / share) with tabular figures, so the columns
    // line up and the tooltip's width doesn't shift as the numbers change between months.
    <Box
      css={{
        display: "grid",
        gridTemplateColumns: "1fr auto auto",
        alignItems: "center",
        columnGap: 12,
        rowGap: 4,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <Text css={{ fontWeight: 600, color: colors.text, colSpan: 3, pb: 2 }}>
        {mrrTooltipMonthFormatter.format(d.month)}
      </Text>
      {rows.map(row => {
        const amount = d.byStream[row.key] ?? 0;
        return (
          <Fragment key={row.key}>
            <Flex css={{ align: "center", gap: 8 }}>
              <Box
                css={{
                  size: 10,
                  borderRadius: 2,
                  backgroundColor: row.color,
                  flexShrink: 0,
                }}
              />
              <Text css={{ color: colors.text2, lineHeight: 1 }}>{row.label}</Text>
            </Flex>
            <Text css={{ color: colors.text, fontWeight: 500, lineHeight: 1, textAlign: "right" }}>
              {formatAmount(amount)}
            </Text>
            <Text css={{ color: colors.text3, lineHeight: 1, textAlign: "right", minWidth: 34 }}>
              {formatShare(amount, d.total)}
            </Text>
          </Fragment>
        );
      })}
      <Box css={{ height: 1, backgroundColor: colors.outline, colSpan: 3, my: 2 }} />
      <Text css={{ color: colors.text2, fontWeight: 600, lineHeight: 1 }}>Total</Text>
      <Text css={{ color: colors.text, fontWeight: 600, lineHeight: 1, textAlign: "right" }}>
        {formatAmount(d.total)}
      </Text>
      <Box />
    </Box>
  );
}

function MRRChartLegend() {
  return (
    <Flex css={{ gap: 16, justify: "center", flexWrap: "wrap" }}>
      {REVENUE_STREAMS.map(item => (
        <Box key={item.key} css={{ display: "flex", alignItems: "center", fontScale: "sm" }}>
          <Box css={{ size: 16, borderRadius: 4, backgroundColor: item.color, mr: 6 }} />
          {item.label}
        </Box>
      ))}
    </Flex>
  );
}

// Target starts from this fixed date
const TARGET_START_DATE = new Date(2025, 11, 11); // Dec 11, 2025
const TARGET_START_VALUE = 1513;

function getTargetData(maxDate: Date): ChartDataPoint[] {
  const result: ChartDataPoint[] = [];

  // Start at Dec 11, 2025 with 1513
  result.push({ date: new Date(TARGET_START_DATE), value: TARGET_START_VALUE, type: "Target" });

  // Generate points for each month end after Dec 2025
  // Month 1 (Jan 31): +100, Month 2 (Feb 28): +200, Month 3 (Mar 31): +300, etc.
  let lastValue = TARGET_START_VALUE;
  for (let i = 1; i <= 24; i++) {
    // Last day of month i months after December 2025
    const date = new Date(2025, 11 + i + 1, 0); // Dec is month 11, so Jan is 12, etc.
    const value = TARGET_START_VALUE + 100 * ((i * (i + 1)) / 2);

    if (date > maxDate) {
      // Add final point at maxDate with interpolated value
      const prevDate = result[result.length - 1]!.date;
      const progress = (maxDate.getTime() - prevDate.getTime()) / (date.getTime() - prevDate.getTime());
      const interpolatedValue = Math.round(lastValue + (value - lastValue) * progress);
      result.push({ date: new Date(maxDate), value: interpolatedValue, type: "Target" });
      break;
    }

    lastValue = value;
    result.push({ date, value, type: "Target" });
  }

  return result;
}

// Members Served = members with an active recurring allowance and/or active EAP coverage.
// The backend pre-aggregates the three mutually-exclusive buckets per day (see obs/members-served),
// so there's no client-side event replay - we just plot the series.

// One row per day carrying all three dimensions, for a single unified tooltip on hover.
type MembersServedDay = {
  date: Date;
  recurring: number;
  eap: number;
  both: number;
  total: number;
};

function MembersServed() {
  const { data } = useSuspenseQuery(membersServedQuery());

  const { today, total, targetData, latest, definition } = useMemo(() => {
    const today = new Date();
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 1);

    // Headline reflects the latest day from the full series, regardless of the charted window.
    const latest = data.points.at(-1);
    const total = latest ? latest.recurring_only + latest.eap_only + latest.eap_and_recurring : 0;

    // The endpoint returns all history; the chart shows a recent window (matching the old widget).
    const windowStart = new Date(today);
    windowStart.setDate(windowStart.getDate() - 180);
    const points = data.points.filter(p => new Date(p.date) >= windowStart);

    // One stacked-area datum per bucket per day. Labels match the legend order below.
    const chartData: ChartDataPoint[] = points.flatMap(p => {
      const date = new Date(p.date);
      return [
        { date, value: p.recurring_only, type: "Recurring" },
        { date, value: p.eap_only, type: "EAP Supported" },
        { date, value: p.eap_and_recurring, type: "Both" },
      ];
    });

    // Extend the bands flat from today to maxDate (holding today's values) so the area stretches to the
    // right edge alongside the target line, rather than stopping at today.
    if (latest) {
      chartData.push(
        { date: new Date(maxDate), value: latest.recurring_only, type: "Recurring" },
        { date: new Date(maxDate), value: latest.eap_only, type: "EAP Supported" },
        { date: new Date(maxDate), value: latest.eap_and_recurring, type: "Both" },
      );
    }

    const dailyTotals: MembersServedDay[] = points.map(p => ({
      date: new Date(p.date),
      recurring: p.recurring_only,
      eap: p.eap_only,
      both: p.eap_and_recurring,
      total: p.recurring_only + p.eap_only + p.eap_and_recurring,
    }));

    const targetData = getTargetData(maxDate);

    // Tallest thing on the chart: the stacked totals (including the flat-held extension) or the
    // target line. The veil spans 0..yTop so it covers the full plot height, and it contributes the
    // same maximum the data already implies, leaving the inferred y domain unchanged.
    const yTop = Math.max(0, ...dailyTotals.map(d => d.total), total, ...targetData.map(d => d.value));

    const minX = new Date(
      Math.min(targetData[0]?.date.getTime() ?? today.getTime(), chartData[0]?.date.getTime() ?? today.getTime()),
    );
    const monthTicks = monthStartsBetween(minX, maxDate);

    const veil: VeilRegion = { start: today, end: maxDate };

    const definition = defineChart({
      marks: [
        ruleY([0], { stroke: colors.outline, strokeOpacity: 1 }),
        // Decorative: painted, but no interaction points - focus and the tooltip attach to the
        // invisible per-day dot below instead, giving one unified tooltip per day.
        decorative(
          areaY(chartData, {
            x: "date",
            y: "value",
            z: "type",
            // Stack bottom→top: Recurring, Both, EAP. "Both" in the middle means everything touching
            // the bottom "has allowances" (Recurring + Both) and everything touching the top "has EAP"
            // (EAP + Both) - easier to read each criterion at a glance.
            layout: stack({ order: ["Recurring", "Both", "EAP Supported"] }),
            fill: d => {
              if (d.type === "EAP Supported") {
                return colors.yellow;
              }
              if (d.type === "Both") {
                return colors.green;
              }
              return colors.cyan;
            },
            fillOpacity: 1,
          }),
        ),
        // Veil the projected region (today → maxDate) so the flat-held bands read as projection,
        // not actual data. Drawn after the area but before the target line, which stays crisp.
        ...(yTop > 0
          ? [
              decorative(
                rect([veil], {
                  x1: "start",
                  x2: "end",
                  y1: () => 0,
                  y2: () => yTop,
                  fill: "#ffffff66",
                  inset: 0,
                }),
              ),
            ]
          : []),
        decorative(
          lineY(targetData, {
            x: "date",
            y: "value",
            stroke: colors.text3,
            strokeWidth: 2,
            strokeDasharray: "6,4",
          }),
        ),
        ruleX([today], { stroke: colors.text3, strokeWidth: 1, strokeOpacity: 1 }),
        // The vertical rule that follows the hovered day (the old Plot.pointerX ruleX).
        crosshair({ x: { stroke: colors.text3, strokeWidth: 1, strokeOpacity: 1 }, y: false }),
        // Invisible dot at each day's stacked total: the only interactive mark, so hovering snaps to
        // the nearest actual day (never the projected region) and the tooltip anchors above the total.
        dot(dailyTotals, { x: "date", y: "total", r: 4, fillOpacity: 0, strokeOpacity: 0 }),
      ],
      scales: {
        x: {
          scale: scaleUtc,
          grid: true,
          axis: {
            line: false,
            ticks: { values: monthTicks, size: 0, format: formatMonthTick },
            tickLabels: { fontSize: 12 },
          },
        },
        y: {
          scale: scaleLinear,
          grid: false,
          axis: { line: false, ticks: { size: 0, format: formatAxisNumber }, tickLabels: { fontSize: 12 } },
        },
      },
      margin: { left: 50, bottom: 40 },
      focus: "nearest-x",
      focusRing: false,
      maxFocusDistance: Number.POSITIVE_INFINITY,
      tooltip: { use: tooltip, placement: "top", offset: 16 },
    });

    return { today, total, targetData, latest, definition };
  }, [data]);

  const { ref, height } = useContainerSize();

  return (
    <Card title="Members Served" css={{ display: "flex", flexDirection: "column", flex: "1 0 0px" }}>
      <BigValue>{formatWholeNumber(total)}</BigValue>
      <MembersServedLegend
        recurring={latest?.recurring_only ?? 0}
        eap={latest?.eap_only ?? 0}
        both={latest?.eap_and_recurring ?? 0}
        nextTarget={targetData.find(d => d.date > today)?.value}
        showTarget
        asOf={data.as_of ?? latest?.updated_at}
      />
      <ChartBox
        ref={ref}
        css={{
          height: "100%",
          flex: "1 0 0px",
          overflow: "hidden",
          color: colors.text,
        }}
      >
        {height !== undefined && (
          <TooltipChart
            definition={definition}
            height={height}
            ariaLabel="Members Served"
            renderTooltipBody={({ points, defaultBody }) => {
              const datum = points[0]?.datum;
              if (!datum || !("total" in datum)) return defaultBody;
              return <MembersServedTooltip d={datum} />;
            }}
          />
        )}
      </ChartBox>
    </Card>
  );
}

// "15 Jun 2026" - dd MMM yyyy.
const tooltipDateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});
const formatTooltipDate = (date: Date) => tooltipDateFormatter.format(date);

function MembersServedTooltip({ d }: { d: MembersServedDay }) {
  const rows = [
    { label: "EAP Supported", value: d.eap, color: colors.yellow },
    { label: "Both", value: d.both, color: colors.green },
    { label: "Recurring", value: d.recurring, color: colors.cyan },
  ];
  return (
    <Flex css={{ flexDirection: "column", gap: 4, minWidth: 180 }}>
      <Text css={{ fontWeight: 600, color: colors.text }}>{formatTooltipDate(d.date)}</Text>
      {rows.map(row => (
        <Flex key={row.label} css={{ align: "center", gap: 8 }}>
          <Box
            css={{ size: 10, borderRadius: 2, backgroundColor: row.color, flexShrink: 0, position: "relative", top: 1 }}
          />
          <Text css={{ color: colors.text2, lineHeight: 1 }}>{row.label}</Text>
          <Text css={{ color: colors.text, fontWeight: 500, lineHeight: 1, ml: "auto" }}>
            {formatWholeNumber(row.value)}
          </Text>
        </Flex>
      ))}
      <Box css={{ height: 1, backgroundColor: colors.outline, my: 2 }} />
      <Flex css={{ align: "center", gap: 8 }}>
        <Box css={{ size: 10, flexShrink: 0 }} />
        <Text css={{ color: colors.text2, fontWeight: 600, lineHeight: 1 }}>Total</Text>
        <Text css={{ color: colors.text, fontWeight: 600, lineHeight: 1, ml: "auto" }}>
          {formatWholeNumber(d.total)}
        </Text>
      </Flex>
    </Flex>
  );
}

function MembersServedLegend({
  recurring,
  eap,
  both,
  nextTarget,
  showTarget = false,
  asOf,
}: {
  recurring: number;
  eap: number;
  both: number;
  nextTarget?: number;
  showTarget?: boolean;
  asOf?: string | null;
}) {
  const legendItems = [
    { label: "EAP Supported", count: eap, color: colors.yellow },
    { label: "Both", count: both, color: colors.green },
    { label: "Recurring", count: recurring, color: colors.cyan },
  ];
  return (
    <Flex css={{ flexDirection: "column", align: "center", gap: 2, pb: 8 }}>
      <Flex css={{ gap: 16, justify: "center" }}>
        {legendItems.map(item => (
          <Box key={item.label} css={{ display: "flex", alignItems: "center", fontScale: "sm" }}>
            <Box css={{ size: 16, borderRadius: 4, backgroundColor: item.color, mr: 6 }} />
            {formatWholeNumber(item.count)} {item.label}
          </Box>
        ))}
        {showTarget && (
          <Box css={{ display: "flex", alignItems: "center", fontScale: "sm" }}>
            <svg width="16" height="16" style={{ marginRight: 6 }}>
              <line x1="0" y1="8" x2="16" y2="8" stroke={colors.text3} strokeWidth="2" strokeDasharray="4,2" />
            </svg>
            {nextTarget != null ? formatWholeNumber(nextTarget) : ""} EOM Target
          </Box>
        )}
      </Flex>
      {asOf && <Text css={{ fontScale: "xs", color: colors.text3 }}>as of {new Date(asOf).toLocaleString()}</Text>}
    </Flex>
  );
}

// Active Supporters = supporters that served at least one live member that day (i.e. have a
// members-served snapshot row), bucketed by which programme(s) they served members through. Same
// three-bucket shape and colours as Members Served, so the two cards read together.
function ActiveSupporters() {
  const { data } = useSuspenseQuery(activeSupportersQuery());

  const { total, latest, definition } = useMemo(() => {
    const today = new Date();

    // Headline reflects the latest day from the full series, regardless of the charted window.
    const latest = data.points.at(-1);
    const total = latest ? latest.recurring_only + latest.eap_only + latest.eap_and_recurring : 0;

    const windowStart = new Date(today);
    windowStart.setDate(windowStart.getDate() - 180);
    const points = data.points.filter(p => new Date(p.date) >= windowStart);

    const chartData: ChartDataPoint[] = points.flatMap(p => {
      const date = new Date(p.date);
      return [
        { date, value: p.recurring_only, type: "Recurring" },
        { date, value: p.eap_only, type: "EAP Supported" },
        { date, value: p.eap_and_recurring, type: "Both" },
      ];
    });

    const dailyTotals: MembersServedDay[] = points.map(p => ({
      date: new Date(p.date),
      recurring: p.recurring_only,
      eap: p.eap_only,
      both: p.eap_and_recurring,
      total: p.recurring_only + p.eap_only + p.eap_and_recurring,
    }));

    const first = dailyTotals[0]?.date ?? today;
    const last = dailyTotals.at(-1)?.date ?? today;
    const monthTicks = monthStartsBetween(first, last);

    const definition = defineChart({
      marks: [
        ruleY([0], { stroke: colors.outline, strokeOpacity: 1 }),
        decorative(
          areaY(chartData, {
            x: "date",
            y: "value",
            z: "type",
            // Same stacking as Members Served: bottom touches "has allowances", top touches "has EAP".
            layout: stack({ order: ["Recurring", "Both", "EAP Supported"] }),
            fill: d => {
              if (d.type === "EAP Supported") {
                return colors.yellow;
              }
              if (d.type === "Both") {
                return colors.green;
              }
              return colors.cyan;
            },
            fillOpacity: 1,
          }),
        ),
        crosshair({ x: { stroke: colors.text3, strokeWidth: 1, strokeOpacity: 1 }, y: false }),
        // Invisible dot at each day's total: the only interactive mark, so hover snaps to a day and the
        // tooltip anchors above the stacked total.
        dot(dailyTotals, { x: "date", y: "total", r: 4, fillOpacity: 0, strokeOpacity: 0 }),
      ],
      scales: {
        x: {
          scale: scaleUtc,
          grid: true,
          axis: {
            line: false,
            ticks: { values: monthTicks, size: 0, format: formatMonthTick },
            tickLabels: { fontSize: 12 },
          },
        },
        y: {
          scale: scaleLinear,
          grid: false,
          axis: { line: false, ticks: { size: 0, format: formatAxisNumber }, tickLabels: { fontSize: 12 } },
        },
      },
      margin: { left: 50, bottom: 40 },
      focus: "nearest-x",
      focusRing: false,
      maxFocusDistance: Number.POSITIVE_INFINITY,
      tooltip: { use: tooltip, placement: "top", offset: 16 },
    });

    return { total, latest, definition };
  }, [data]);

  const { ref, height } = useContainerSize();

  return (
    <Card title="Active Supporters" css={{ display: "flex", flexDirection: "column" }}>
      <BigValue>{formatWholeNumber(total)}</BigValue>
      <MembersServedLegend
        recurring={latest?.recurring_only ?? 0}
        eap={latest?.eap_only ?? 0}
        both={latest?.eap_and_recurring ?? 0}
        asOf={data.as_of ?? latest?.updated_at}
      />
      <ChartBox
        ref={ref}
        css={{
          height: "100%",
          flex: "1 0 0px",
          overflow: "hidden",
          color: colors.text,
        }}
      >
        {height !== undefined && (
          <TooltipChart
            definition={definition}
            height={height}
            ariaLabel="Active Supporters"
            renderTooltipBody={({ points, defaultBody }) => {
              const datum = points[0]?.datum;
              if (!datum || !("total" in datum)) return defaultBody;
              return <MembersServedTooltip d={datum} />;
            }}
          />
        )}
      </ChartBox>
    </Card>
  );
}

const currencyChartColors = [
  colors.cyan,
  colors.dataviz.quarterly,
  colors.dataviz.annual,
  colors.dataviz.oneOff,
  colors.dataviz.biannual,
];

type CurrencyRatioPoint = { date: Date; ratio: number; currency: string };

function CurrencyRatioChart() {
  const { data } = useSuspenseQuery({
    queryKey: ["reporting", "currency-ratio"],
    queryFn: () => api.reporting.getManagementReportingObsCurrencyRatio({ currencies: ["nzd", "aud", "usd"] }),
  });

  const isBelowThreshold = data.currencies.some(
    c => c.time_series.length > 0 && c.time_series.at(-1)!.ratio < data.alarm_threshold,
  );

  const definition = useMemo(() => {
    const chartData: CurrencyRatioPoint[] = data.currencies.flatMap(series =>
      series.time_series.map((d: { timestamp_utc: string; ratio: number }) => ({
        date: new Date(d.timestamp_utc),
        ratio: d.ratio,
        currency: series.currency.toUpperCase(),
      })),
    );

    const colorDomain = data.currencies.map(c => c.currency.toUpperCase());
    const colorRange = colorDomain.map((_, i) => currencyChartColors[i % currencyChartColors.length] ?? colors.cyan);

    const threshold = data.alarm_threshold;
    // Anchor the annotation's right edge to the last charted date (the right edge of the plot),
    // reproducing Plot's frameAnchor: "right".
    const lastDate = chartData.length ? new Date(Math.max(...chartData.map(d => d.date.getTime()))) : undefined;

    return defineChart({
      marks: [
        ruleY([threshold], { stroke: colors.red500, strokeDasharray: "4 4", strokeOpacity: 1 }),
        ...(lastDate === undefined
          ? []
          : [
              // Decorative so the annotation never steals focus or the tooltip from the lines.
              decorative(
                text([threshold], {
                  x: () => lastDate,
                  y: t => t,
                  text: () => `${threshold}% Alert threshold`,
                  anchor: "end",
                  dy: -10,
                  fill: colors.red500,
                  fontSize: 11,
                }),
              ),
            ]),
        lineY(chartData, {
          x: "date",
          y: "ratio",
          color: "currency",
          strokeWidth: 2,
        }),
      ],
      scales: {
        x: {
          scale: scaleUtc,
          axis: { line: false, ticks: { size: 0 }, tickLabels: { fontSize: 12 } },
        },
        y: {
          scale: scaleLinear().domain([-5, 105]),
          grid: true,
          axis: { line: false, ticks: { size: 0, format: value => `${value} %` }, tickLabels: { fontSize: 12 } },
        },
      },
      margin: { left: 50, bottom: 40 },
      color: { domain: colorDomain, range: colorRange },
      tooltip: {
        use: tooltip,
        content: points => {
          const point = points[0];
          if (!point || typeof point.datum === "number") return { rows: [] };
          return {
            title: `${point.datum.currency} ${point.datum.date.toLocaleTimeString()}`,
            rows: [{ label: "", value: `${point.datum.ratio.toFixed(1)}%` }],
          };
        },
      },
    });
  }, [data]);

  const { ref, height } = useContainerSize();

  return (
    <Card css={{ display: "flex", flexDirection: "column", pt: 0 }}>
      <Box
        css={{
          bg: isBelowThreshold ? colors.red50 : undefined,
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          pt: 16,
          pb: 4,
        }}
      >
        <Text
          css={{
            fontSize: 25,
            weight: 500,
            lineHeight: 1.25,
            align: "center",
            color: colors.navy,
            mb: 4,
          }}
        >
          AirWallex:Minted Ratio
        </Text>
        <CurrencyRatioLegend
          currencies={data.currencies.map((c, i) => ({
            currency: c.currency,
            currentRatio: c.time_series.at(-1)?.ratio ?? null,
            color: currencyChartColors[i % currencyChartColors.length]!,
          }))}
        />
      </Box>
      <ChartBox
        ref={ref}
        css={{
          height: "100%",
          flex: "1 0 0px",
          overflow: "hidden",
          color: colors.text,
        }}
      >
        {height !== undefined && <Chart definition={definition} height={height} ariaLabel="AirWallex:Minted Ratio" />}
      </ChartBox>
    </Card>
  );
}

function CurrencyRatioLegend({
  currencies,
}: {
  currencies: { currency: string; currentRatio: number | null; color: string }[];
}) {
  return (
    <Flex css={{ gap: 24, justify: "center" }}>
      {currencies.map(c => (
        <Box key={c.currency} css={{ display: "flex", alignItems: "center", fontScale: "sm" }}>
          <Box css={{ size: 20, borderRadius: 6, backgroundColor: c.color, mr: 8 }} />
          {c.currency.toUpperCase()} {c.currentRatio != null ? `${c.currentRatio.toFixed(1)}%` : "—"}
        </Box>
      ))}
    </Flex>
  );
}

const BigValue = styled("p", {
  fontSize: 72,
  weight: 900,
  align: "center",
  lineHeight: 1,
  fontFamily: fonts.ftHabit,
});

const ValueDescription = styled("p", {
  fontSize: 14,
  weight: 500,
  align: "center",
  lineHeight: 1,
  color: colors.text2,
});

type CardProps = Omit<BoxProps, "title"> & {
  title?: ReactNode;
};
function Card({ title, children, ...props }: CardProps) {
  return (
    <Box
      {...props}
      css={{
        pt: 16,
        rounded: 12,
        bg: "white",
        display: "flex",
        flexDirection: "column",
        boxShadow: `0 0 0 1px rgba(0,0,0,0.05)`,
        ...props.css,
      }}
    >
      {title && (
        <Text
          css={{
            fontSize: 25,
            weight: 500,
            lineHeight: 1.25,
            align: "center",
            color: colors.navy,
            mb: 4,
          }}
        >
          {title}
        </Text>
      )}
      {children}
    </Box>
  );
}
